import { useEffect, useRef } from "react";

import {
  PLANT_UPDATED_EVENT,
  WBS_UPDATED_EVENT,
} from "../components/Drawers/TreeViewDrawer/treeView/utils/projectSavedEvent";

/**
 * The projects tree owns the project and WBS records, but everything that
 * shows them keeps its own copy, fetched when the project was opened: the
 * name and description in a module header, the options in a WBS picker, the
 * two names every Rev UID is built from. These subscribe those copies to the
 * tree's edits, so a rename shows everywhere at once instead of on the next
 * reload.
 *
 * The event carries what changed, so a listener updates in place rather than
 * re-fetching - that is what makes it instant.
 */

// Subscribes to one event, filtered to one id, without re-subscribing when an
// inline handler is passed.
const useEditSubscription = (eventName, idKey, id, onUpdated) => {
  const handlerRef = useRef(onUpdated);
  handlerRef.current = onUpdated;

  useEffect(() => {
    // `null` id means "every edit of this kind" - see useAnyWbsUpdated.
    if (id === undefined || id === "") return undefined;

    const handle = (event) => {
      const detail = event?.detail;
      const updatedId = detail?.[idKey];
      if (updatedId == null) return;
      if (id !== null && String(updatedId) !== String(id)) return;

      handlerRef.current?.({
        [idKey]: updatedId,
        changes: detail?.changes || {},
      });
    };

    window.addEventListener(eventName, handle);
    return () => window.removeEventListener(eventName, handle);
  }, [eventName, idKey, id]);
};

/** The WBS with this id was renamed, or its description rewritten. */
export default function useWbsUpdated(projectId, onUpdated) {
  useEditSubscription(WBS_UPDATED_EVENT, "projectId", projectId, onUpdated);
}

/**
 * Any WBS was edited - for the pickers, which list every WBS of a plant and
 * so care about edits to ones they don't currently have selected.
 */
export function useAnyWbsUpdated(onUpdated) {
  useEditSubscription(WBS_UPDATED_EVENT, "projectId", null, onUpdated);
}

/** The project (plant) with this id was renamed, or its description rewritten. */
export function usePlantUpdated(plantId, onUpdated) {
  useEditSubscription(PLANT_UPDATED_EVENT, "plantId", plantId, onUpdated);
}
